import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type { ViewMode } from '@/lib/view-mode';
import type { Region } from '@/lib/graph-regions';

// Which dialog-derived JTBD nodes to show: 'all' both, 'raw' the unprocessed
// dialog extractions, 'synthesised' the promoted clusters. Nodes that aren't
// dialog-derived (idea, capability, manually-added) are unaffected by this axis.
export type NodeSourceFilter = 'all' | 'raw' | 'synthesised';
// Which respondent-context dimension to slice the graph by. v1 ships with
// 'role' as the only choice the UI exposes, but the data layer carries the
// full snapshot — adding a group-by picker later is purely a UI change.
export type SegmentField = 'role' | 'team' | 'company';
// Sentinel value used in segmentFilter to represent nodes that have no
// segment for the current segmentField (anonymous dialog nodes, PM-authored
// nodes, and legacy unsegmented nodes). Picked to be unmistakable for any real
// human-typed value so a literal context field of "Unsegmented" can't
// collide with it. Exported so the filter UI and the matching logic agree.
export const UNSEGMENTED = '__unsegmented__';

interface UIState {
  viewMode: ViewMode;
  isSidebarOpen: boolean;
  // Persisted: whether the live graph-peek panel is shown alongside step
  // workspaces. Default open — watching the graph grow as steps complete is
  // the product's core mental model, so it's visible until dismissed.
  isGraphPeekOpen: boolean;
  // Persisted: the one-time "how this works" intro strip on the idea
  // workspace has been dismissed. Global, not per-project — the arc is the
  // same everywhere and re-teaching it per project would nag.
  hasSeenJourneyIntro: boolean;
  activeModal: string | null;
  // Which dialog-derived nodes to show on the canvas (see NodeSourceFilter).
  // Edges that touch hidden nodes are also hidden so the canvas doesn't render
  // dangling lines. Not persisted — each session starts fresh on 'synthesised'
  // (the primary view: promoted themes + still-un-clustered raw findings, so
  // nothing silently disappears; identical to 'all' until synthesis runs).
  nodeSourceFilter: NodeSourceFilter;
  // Whether to show the evidence overlay (market_signal + assumption nodes).
  // These attach to the idea/spine and pile up on the left; hiding them lets
  // the PM focus on the JTBD spine. Not persisted (fresh 'shown' each session).
  showEvidence: boolean;
  // Which respondent-context field defines a "segment" for filtering. Only
  // 'role' is exposed in the v1 UI; the rest are infrastructure for the
  // group-by picker we'll add once we have real segmented data to inform it.
  segmentField: SegmentField;
  // Selected segment values to keep visible. Empty array = no segment filter
  // (show all). Includes UNSEGMENTED to opt nodes without a segment in or
  // out of the visible set.
  segmentFilter: string[];
  // Whether the signed-in caller may mutate the active project. Mirrors the
  // backend role gate (false for a viewer) and drives read-only mode on the
  // canvas / node modal. Set per-project from the project's callerRole; not
  // persisted — recomputed on load, and a stale `false` would lock out an
  // owner. Defaults true so editing isn't briefly blocked before the role loads.
  canEdit: boolean;
  // One-shot handoff from the peek dot map (zone/dot click): FlowCanvas
  // frames this region on its next mount, then clears it. Not persisted.
  graphFocusRegion: Region | null;

  setViewMode: (mode: ViewMode) => void;
  toggleSidebar: () => void;
  setIsGraphPeekOpen: (open: boolean) => void;
  setHasSeenJourneyIntro: (seen: boolean) => void;
  setActiveModal: (modal: string | null) => void;
  setNodeSourceFilter: (filter: NodeSourceFilter) => void;
  setShowEvidence: (show: boolean) => void;
  setSegmentField: (field: SegmentField) => void;
  setSegmentFilter: (values: string[]) => void;
  setCanEdit: (canEdit: boolean) => void;
  setGraphFocusRegion: (region: Region | null) => void;
  reset: () => void;
}

export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        // Placeholder until ProjectPage picks the real landing (?view= param
        // or the first not-done step) — never rendered, the page gates on it.
        viewMode: 'idea',
        isSidebarOpen: true,
        isGraphPeekOpen: true,
        hasSeenJourneyIntro: false,
        activeModal: null,
        // Synthesised is the primary view: promoted themes plus any still
        // un-clustered raw findings (so nothing silently disappears), with the
        // evidence overlay on. With nothing synthesised yet it renders
        // identically to All, so it's safe as the day-one default too.
        nodeSourceFilter: 'synthesised' as NodeSourceFilter,
        showEvidence: true,
        segmentField: 'role' as SegmentField,
        segmentFilter: [],
        canEdit: true,
        graphFocusRegion: null,

        setViewMode: (mode) => set({ viewMode: mode }),
        toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
        setIsGraphPeekOpen: (open) => set({ isGraphPeekOpen: open }),
        setHasSeenJourneyIntro: (seen) => set({ hasSeenJourneyIntro: seen }),

        setActiveModal: (modal) => set({ activeModal: modal }),
        setNodeSourceFilter: (filter) => set({ nodeSourceFilter: filter }),
        setShowEvidence: (show) => set({ showEvidence: show }),
        setSegmentField: (field) => set({ segmentField: field, segmentFilter: [] }),
        setSegmentFilter: (values) => set({ segmentFilter: values }),
        setCanEdit: (canEdit) => set({ canEdit }),
        setGraphFocusRegion: (region) => set({ graphFocusRegion: region }),

        reset: () =>
          set({
            viewMode: 'idea',
            isSidebarOpen: true,
            activeModal: null,
            nodeSourceFilter: 'synthesised',
            showEvidence: true,
            segmentField: 'role',
            segmentFilter: [],
            canEdit: true,
            graphFocusRegion: null,
          }),
      }),
      {
        name: 'valuegraph-ui',
        partialize: (state) => ({
          isSidebarOpen: state.isSidebarOpen,
          isGraphPeekOpen: state.isGraphPeekOpen,
          hasSeenJourneyIntro: state.hasSeenJourneyIntro,
        }),
      }
    ),
    { name: 'ui-store' }
  )
);
